import { useEffect, useState } from "react";
import axios from "axios";
import { API } from "../App";
import { Plus, Eye, Send, ExternalLink, Edit } from "lucide-react";
import { toast } from "sonner";

export default function Articles() {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [editInstruction, setEditInstruction] = useState("");
  const [formData, setFormData] = useState({
    source_url: "",
    title: "",
    keywords: ""
  });
  const [generating, setGenerating] = useState(false);
  const [publishing, setPublishing] = useState(null);
  const [editing, setEditing] = useState(false);

  useEffect(() => {
    fetchArticles();
  }, []);

  const fetchArticles = async () => {
    try {
      const response = await axios.get(`${API}/articles`);
      setArticles(response.data);
    } catch (error) {
      console.error("Erreur chargement articles:", error);
      toast.error("Erreur de chargement");
    } finally {
      setLoading(false);
    }
  };

  const handleGenerate = async (e) => {
    e.preventDefault();
    setGenerating(true);
    
    try {
      const keywords = formData.keywords.split(',').map(k => k.trim()).filter(k => k);
      await axios.post(`${API}/articles/generate`, {
        source_url: formData.source_url,
        title: formData.title || undefined,
        keywords: keywords
      });
      
      toast.success("Article généré avec succès !");
      setShowGenerateModal(false);
      setFormData({ source_url: "", title: "", keywords: "" });
      fetchArticles();
    } catch (error) {
      console.error("Erreur génération:", error);
      
      // Gérer les erreurs de validation ou autres
      let errorMessage = "Erreur lors de la génération";
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        if (typeof detail === 'object') {
          errorMessage = JSON.stringify(detail);
        } else {
          errorMessage = detail;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast.error(errorMessage);
    } finally {
      setGenerating(false);
    }
  };

  const handlePublish = async (articleId) => {
    if (!window.confirm("Publier cet article sur WordPress ?")) return;
    
    setPublishing(articleId);
    try {
      const response = await axios.post(`${API}/wordpress/publish/${articleId}`);
      toast.success("Article publié sur WordPress !");
      fetchArticles();
    } catch (error) {
      console.error("Erreur publication:", error);
      
      let errorMessage = "Erreur lors de la publication";
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        if (typeof detail === 'object') {
          errorMessage = JSON.stringify(detail);
        } else {
          errorMessage = detail;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast.error(errorMessage);
    } finally {
      setPublishing(null);
    }
  };

  const handlePreview = (article) => {
    setSelectedArticle(article);
    setShowPreviewModal(true);
  };

  const handleEdit = (article) => {
    setSelectedArticle(article);
    setEditInstruction("");
    setShowEditModal(true);
  };

  const handleEditWithAI = async () => {
    if (!editInstruction.trim()) {
      toast.error("Veuillez entrer une instruction");
      return;
    }

    setEditing(true);
    try {
      toast.info("Modification en cours avec l'IA... (30-60s)");
      
      await axios.post(`${API}/articles/${selectedArticle.id}/edit`, {
        instruction: editInstruction
      });
      
      toast.success("Article modifié avec succès !");
      setShowEditModal(false);
      setEditInstruction("");
      fetchArticles();
    } catch (error) {
      console.error("Erreur modification:", error);
      toast.error(error.response?.data?.detail || "Erreur lors de la modification");
    } finally {
      setEditing(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96" data-testid="articles-loading">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 fade-in" data-testid="articles-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold text-gray-900 mb-2">Articles</h1>
          <p className="text-gray-600">Gérez vos articles générés par IA</p>
        </div>
        <button
          onClick={() => setShowGenerateModal(true)}
          className="btn-primary flex items-center space-x-2"
          data-testid="generate-article-button"
        >
          <Plus size={20} />
          <span>Générer un article</span>
        </button>
      </div>

      {articles.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-100 p-12 text-center" data-testid="no-articles">
          <p className="text-gray-600 mb-4">Aucun article généré</p>
          <button onClick={() => setShowGenerateModal(true)} className="btn-primary">
            Générer votre premier article
          </button>
        </div>
      ) : (
        <div className="table-container" data-testid="articles-table">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr>
                  <th className="table-header text-left">Titre</th>
                  <th className="table-header text-left">Catégorie</th>
                  <th className="table-header text-left">Statut</th>
                  <th className="table-header text-left">Mots-clés</th>
                  <th className="table-header text-left">Date</th>
                  <th className="table-header text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {articles.map((article) => (
                  <tr key={article.id} className="table-row" data-testid={`article-${article.id}`}>
                    <td className="px-6 py-4">
                      <div className="font-medium text-gray-900">{article.title}</div>
                      <div className="text-sm text-gray-500 truncate max-w-md">{article.meta_description}</div>
                      {article.price_range && (
                        <div className="text-xs text-[#4F86EF] font-medium mt-1">
                          💰 {article.price_range}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-sm bg-blue-50 text-blue-700 px-3 py-1 rounded-full font-medium">
                        {article.category || 'Non catégorisé'}
                      </span>
                      {article.estimated_reading_time && (
                        <div className="text-xs text-gray-500 mt-1">
                          📖 {article.estimated_reading_time} min
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <span className={`badge ${
                        article.status === 'published' ? 'badge-success' :
                        article.status === 'ready' ? 'badge-info' : 'badge-default'
                      }`}>
                        {article.status === 'published' ? 'Publié' :
                         article.status === 'ready' ? 'Prêt' : 'Brouillon'}
                      </span>
                      {article.wordpress_url && (
                        <a
                          href={article.wordpress_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs text-[#4F86EF] hover:underline flex items-center space-x-1 mt-1"
                        >
                          <span>Voir sur WP</span>
                          <ExternalLink size={12} />
                        </a>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-wrap gap-1">
                        {article.keywords.slice(0, 3).map((kw, i) => (
                          <span key={i} className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded">
                            {kw}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">
                      {new Date(article.created_at).toLocaleDateString('fr-FR')}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end space-x-2">
                        <button
                          onClick={() => handlePreview(article)}
                          className="btn-secondary text-sm flex items-center space-x-1"
                          data-testid={`preview-${article.id}`}
                        >
                          <Eye size={16} />
                          <span>Aperçu</span>
                        </button>
                        <button
                          onClick={() => handleEdit(article)}
                          className="text-blue-600 hover:text-blue-700 px-3 py-2 rounded-lg hover:bg-blue-50 transition flex items-center space-x-1"
                          title="Modifier avec l'IA"
                        >
                          <Edit size={16} />
                          <span className="text-sm">Modifier</span>
                        </button>
                        {article.status !== 'published' && (
                          <button
                            onClick={() => handlePublish(article.id)}
                            disabled={publishing === article.id}
                            className="btn-primary text-sm flex items-center space-x-1 disabled:opacity-50"
                            data-testid={`publish-${article.id}`}
                          >
                            <Send size={16} />
                            <span>{publishing === article.id ? 'Publication...' : 'Publier'}</span>
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Generate Modal */}
      {showGenerateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-testid="generate-modal">
          <div className="bg-white rounded-2xl p-8 max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Générer un article</h2>
            <form onSubmit={handleGenerate} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">URL source *</label>
                <input
                  type="url"
                  value={formData.source_url}
                  onChange={(e) => setFormData({ ...formData, source_url: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4F86EF] focus:border-transparent"
                  placeholder="https://www.exemple.com/article"
                  required
                  data-testid="generate-url-input"
                />
                <p className="text-xs text-gray-500 mt-1">L'article sera généré en s'inspirant de cette source</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Mots-clés cibles</label>
                <input
                  type="text"
                  value={formData.keywords}
                  onChange={(e) => setFormData({ ...formData, keywords: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4F86EF] focus:border-transparent"
                  placeholder="prix pose, devis, installation"
                  data-testid="generate-keywords-input"
                />
                <p className="text-xs text-gray-500 mt-1">Séparez les mots-clés par des virgules (4-6 recommandés)</p>
              </div>
              <div className="flex space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowGenerateModal(false)}
                  className="btn-secondary flex-1"
                  data-testid="cancel-generate"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  disabled={generating}
                  className="btn-primary flex-1 disabled:opacity-50"
                  data-testid="submit-generate"
                >
                  {generating ? "Génération..." : "Générer"}
                </button>
              </div>
            </form>
            {generating && (
              <div className="mt-4 text-center text-sm text-gray-600">
                ⌛ Génération en cours (cela peut prendre 30-60 secondes)...
              </div>
            )}
          </div>
        </div>
      )}

      {/* Preview Modal */}
      {showPreviewModal && selectedArticle && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="preview-modal">
          <div className="bg-white rounded-2xl p-8 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-900">Aperçu de l'article</h2>
              <button
                onClick={() => setShowPreviewModal(false)}
                className="text-gray-400 hover:text-gray-600"
                data-testid="close-preview"
              >
                ✕
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <h3 className="text-sm font-medium text-gray-700 mb-1">Titre</h3>
                <p className="text-lg font-bold text-gray-900">{selectedArticle.title}</p>
              </div>
              <div>
                <h3 className="text-sm font-medium text-gray-700 mb-1">Meta Title</h3>
                <p className="text-sm text-gray-600">{selectedArticle.meta_title}</p>
              </div>
              <div>
                <h3 className="text-sm font-medium text-gray-700 mb-1">Meta Description</h3>
                <p className="text-sm text-gray-600">{selectedArticle.meta_description}</p>
              </div>
              <div>
                <h3 className="text-sm font-medium text-gray-700 mb-1">Mots-clés</h3>
                <div className="flex flex-wrap gap-2">
                  {selectedArticle.keywords.map((kw, i) => (
                    <span key={i} className="badge badge-info">{kw}</span>
                  ))}
                </div>
              </div>
              <div>
                <h3 className="text-sm font-medium text-gray-700 mb-1">Contenu</h3>
                <div 
                  className="prose max-w-none text-sm"
                  dangerouslySetInnerHTML={{ __html: selectedArticle.content }}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && selectedArticle && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-8 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Modifier l'article avec l'IA</h2>
            <p className="text-gray-600 mb-6">Article : {selectedArticle.title}</p>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Instruction de modification *
                </label>
                <textarea
                  value={editInstruction}
                  onChange={(e) => setEditInstruction(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4F86EF] focus:border-transparent"
                  rows={6}
                  placeholder="Ex: Ajoute une section sur les avantages écologiques après l'introduction. Rends le ton plus accessible et ajoute 2 tableaux comparatifs supplémentaires."
                  disabled={editing}
                />
                <p className="text-xs text-gray-500 mt-2">
                  💡 Exemples : "Ajoute une section FAQ à la fin", "Rends le ton plus professionnel", "Ajoute un tableau comparatif après l'intro"
                </p>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="text-sm font-semibold text-blue-900 mb-2">ℹ️ Conseils</h4>
                <ul className="text-xs text-blue-800 space-y-1">
                  <li>• Soyez précis dans vos instructions</li>
                  <li>• Indiquez où ajouter le contenu (début, fin, après telle section)</li>
                  <li>• L'IA garde la structure HTML existante</li>
                  <li>• Durée : 30-60 secondes selon la complexité</li>
                </ul>
              </div>

              <div className="flex items-center space-x-4 pt-4">
                <button
                  type="button"
                  onClick={() => setShowEditModal(false)}
                  disabled={editing}
                  className="flex-1 btn-secondary disabled:opacity-50"
                >
                  Annuler
                </button>
                <button
                  onClick={handleEditWithAI}
                  disabled={editing || !editInstruction.trim()}
                  className="flex-1 btn-primary disabled:opacity-50 flex items-center justify-center space-x-2"
                >
                  {editing ? (
                    <>
                      <div className="loading-spinner-small"></div>
                      <span>Modification en cours...</span>
                    </>
                  ) : (
                    <>
                      <Edit size={18} />
                      <span>Modifier avec l'IA</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <style jsx>{`
        .loading-spinner-small {
          border: 2px solid #f3f4f6;
          border-top: 2px solid #4F86EF;
          border-radius: 50%;
          width: 18px;
          height: 18px;
          animation: spin 1s linear infinite;
        }
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}
